import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, EditOutlined } from '@mui/icons-material';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useArticlesGetArticlesMutation,
  useArticlesUpdateMutation,
} from '~/api/oneBoxV3';
import type {
  TArticleEduCode,
  TArticleSearchDto,
} from '~/api/oneBoxV3/oneBoxV3.type';
import type { TLatestNewsItem } from './latestNewsStorage';
import { LatestNewsEditDialog } from './LatestNewsEditDialog';
import {
  articleDtoToLatestNewsItem,
  defaultArticlePagination,
  snapshotLatestNewsItemForEdit,
} from './latestNewsArticleMap';
import {
  emptyLatestNewsAdminFilter,
  isLatestNewsAdminFilterAll,
  LatestNewsAdminFilter,
} from './LatestNewsAdminFilter';
import type { TLatestNewsAdminFilterValue } from './LatestNewsAdminFilter';

/**
 * 將後台篩選列轉成後端 `articlesGetArticles` 的 `search`。
 * 任一欄位為「全部」（或空）時不帶該欄位。
 */
const buildAdminArticleSearch = (
  filter: TLatestNewsAdminFilterValue,
): TArticleSearchDto | undefined => {
  const search: TArticleSearchDto = {};
  if (!isLatestNewsAdminFilterAll(filter.eduCode)) {
    search.eduCodes = [filter.eduCode as TArticleEduCode];
  }
  if (!isLatestNewsAdminFilterAll(filter.subject)) {
    search.subjects = [filter.subject];
  }
  if (!isLatestNewsAdminFilterAll(filter.schoolYear)) {
    search.academicYears = [filter.schoolYear];
  }
  if (!isLatestNewsAdminFilterAll(filter.grade)) {
    search.grades = [filter.grade];
  }
  return Object.keys(search).length ? search : undefined;
};

/**
 * /latestnews 後台編輯列表（需後台權限，由 LatestNewsLayout 守門）。
 */
export const LatestNews = () => {
  const [items, setItems] = useState<TLatestNewsItem[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<TLatestNewsItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TLatestNewsItem | null>(
    null,
  );
  const [filter, setFilter] = useState<TLatestNewsAdminFilterValue>(
    emptyLatestNewsAdminFilter,
  );
  /** 用來忽略過時的載入結果（避免快速切換條件造成最後資料錯亂） */
  const loadTokenRef = useRef(0);

  const [getArticles] = useArticlesGetArticlesMutation();
  const [updateArticle] = useArticlesUpdateMutation();

  const loadList = useCallback(async () => {
    const token = ++loadTokenRef.current;
    setListError(null);
    const acc: TLatestNewsItem[] = [];
    const search = buildAdminArticleSearch(filter);
    try {
      let page = 1;
      for (;;) {
        const res = await getArticles({
          pagination: { ...defaultArticlePagination(), page },
          search,
        }).unwrap();
        if (loadTokenRef.current !== token) return;
        if (res.err !== 0) {
          setListError(res.msg ?? `讀取失敗（錯誤碼 ${res.err}）`);
          setItems([]);
          return;
        }
        const chunk = res.data;
        if (!chunk?.data) {
          setItems([]);
          return;
        }
        acc.push(...chunk.data.map(articleDtoToLatestNewsItem));
        if (page >= chunk.totalPages) break;
        page += 1;
      }
      if (loadTokenRef.current !== token) return;
      setItems(acc);
    } catch {
      if (loadTokenRef.current !== token) return;
      setListError('無法連線或取得文章列表，請稍後再試。');
      setItems([]);
    }
  }, [getArticles, filter]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const sorted = [...items].sort((a, b) => b.updatedAt - a.updatedAt);
  const hasActiveFilter =
    !isLatestNewsAdminFilterAll(filter.eduCode) ||
    !isLatestNewsAdminFilterAll(filter.subject) ||
    !isLatestNewsAdminFilterAll(filter.schoolYear) ||
    !isLatestNewsAdminFilterAll(filter.grade);

  const openCreate = () => {
    setDialogMode('create');
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row: TLatestNewsItem) => {
    setDialogMode('edit');
    setEditing(snapshotLatestNewsItemForEdit(row));
    setDialogOpen(true);
  };

  const confirmDemoteToDraft = async () => {
    if (!deleteTarget) return;
    try {
      const res = await updateArticle({
        id: deleteTarget.id,
        status: 'draft',
      }).unwrap();
      if (res.err !== 0) {
        window.alert(res.msg ?? `更新失敗（錯誤碼 ${res.err}）`);
        return;
      }
      setDeleteTarget(null);
      await loadList();
    } catch {
      window.alert('更新失敗，請稍後再試。');
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h5" component="h1" fontWeight={700}>
          最新消息管理
        </Typography>
        <Button
          variant="contained"
          color="info"
          startIcon={<Add />}
          onClick={openCreate}
        >
          新增
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <LatestNewsAdminFilter value={filter} onChange={setFilter} />
      </Box>

      {listError && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {listError}
        </Typography>
      )}

      {sorted.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {hasActiveFilter
              ? '目前篩選條件下沒有符合的消息，請調整條件或選擇「全部」。'
              : '尚無消息，請按右上角「新增」建立。'}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ overflow: 'hidden' }}>
          {sorted.map(row => (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Typography sx={{ flex: 1, fontWeight: 600 }}>
                {row.title}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 1, color: 'text.secondary' }}
                >
                  {row.status === 'draft' ? '草稿' : '已發布'}
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <IconButton
                  size="small"
                  color="info"
                  aria-label="編輯"
                  onClick={() => openEdit(row)}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  aria-label="改為草稿"
                  onClick={() => setDeleteTarget(row)}
                  disabled={row.status === 'draft'}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      {dialogOpen ? (
        <LatestNewsEditDialog
          open
          mode={dialogMode}
          initial={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
          onSaved={async () => {
            await loadList();
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      ) : null}

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>將文章改為草稿</DialogTitle>
        <DialogContent>
          {deleteTarget
            ? `「${deleteTarget.title}」將改為草稿，前台將不再顯示（後端未提供刪除 API，無法從資料庫移除）。`
            : ''}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>取消</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDemoteToDraft}
          >
            改為草稿
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
